/**
 * FiltersView Component
 *
 * This file defines the FiltersView component, which provides a user interface
 * for applying filters through a combination of checkboxes and a date picker.
 * Users can select categories, choose a date, and reset or apply their filter choices.
 *
 * Following the Parkfinder pattern: the store is updated immediately on every
 * toggle, so navigating back (via any method — in-app button, browser back)
 * works correctly without needing local state synchronization.
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { View, Text, StyleSheet, ScrollView, Platform } from "react-native";
import Checkbox from "../../../components/Checkbox/Checkbox";
import { COLORS, STRINGS } from "../../../constants";
import EventStore from "../../../storage/eventStore";
import { useNavigation } from "@react-navigation/native";
import { ActivityIndicator } from "react-native-paper";
import { useRoute } from "@react-navigation/native";
import { logAnalyticsEvent } from "../../../utils/firebase";

const FiltersView = () => {
  logAnalyticsEvent("screen_view", { name: "VBGo-FiltersView" });

  const categories = EventStore((state) => state.categories);
  const fetchCategories = EventStore((state) => state.fetchCategories);
  const selectedCategories = EventStore((state) => state.selectedCategories);
  const loadingCategories = EventStore((state) => state.loadingCategories);

  const navigation = useNavigation();
  const route = useRoute();

  const selectedFromDate = EventStore((state) => state.selectedFromDate);
  const selectedToDate = EventStore((state) => state.selectedToDate);

  const [fromDate, setFromDate] = useState(selectedFromDate ? new Date(selectedFromDate) : null);
  const [toDate, setToDate] = useState(selectedToDate ? new Date(selectedToDate) : null);

  /**
   * handleCheckboxChange
   *
   * Toggles a category directly in the store (Parkfinder pattern).
   * Uses EventStore.getState() to always read the current store value,
   * avoiding stale closure issues with rapid toggling.
   */
  const handleCheckboxChange = useCallback((category) => {
    const current = EventStore.getState().selectedCategories;
    const updated = current.includes(category) ? current.filter((c) => c !== category) : [...current, category];
    EventStore.setState({ selectedCategories: updated });
  }, []);

  /**
   * handleReset
   *
   * Resets all filters in the store immediately.
   */
  const handleReset = useCallback(() => {
    EventStore.setState({ selectedCategories: [] });
    setFromDate("");
    setToDate("");
  }, []);

  /**
   * Effect: Detect "Clear All" action — clears categories and dates.
   * Since we update the store directly, this just resets everything.
   */
  useEffect(() => {
    if (route.params?.clearFilters) {
      handleReset();
      navigation.setParams({ clearFilters: false });
    }
  }, [route.params?.clearFilters]);

  /**
   * Effect: Fetches Categories List on mount
   */
  useEffect(() => {
    fetchCategories();
  }, []);

  /**
   * Web only: Push a history entry on mount so that the browser back button
   * navigates back to ListView instead of leaving the app entirely.
   * Listens for the popstate event to trigger navigation back.
   */
  useEffect(() => {
    if (Platform.OS !== "web") return;

    // Push a history entry for this screen
    window.history.pushState({ screen: "FiltersView" }, "");

    const handlePopState = () => {
      // Browser back was pressed — navigate back to ListView
      const currentCategories = EventStore.getState().selectedCategories;
      const params = currentCategories.length > 0 ? { category: currentCategories } : {};
      navigation.navigate("ListView", params);
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [navigation]);

  const renderCategories = useMemo(() => {
    return categories.map((category) => (
      <View key={category.name} style={styles.checkboxContainer}>
        <Checkbox
          label={category.name}
          value={selectedCategories.includes(category.name)}
          onChange={() => handleCheckboxChange(category.name)}
          color={COLORS.primary}
          uncheckedColor={COLORS.border}
        />
      </View>
    ));
  }, [selectedCategories, categories]);

  return (
    <ScrollView
      style={styles.screen}
      contentContainerStyle={styles.scrollContainer}
      keyboardShouldPersistTaps="handled"
    >
      {/* Category Selection */}
      <View style={styles.container}>
        <Text style={styles.heading}>{STRINGS.category}</Text>

        {loadingCategories ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator animating={true} size="small" color={COLORS.primary} />
            <Text style={styles.loadingText}>{STRINGS.loadingCategories}</Text>
          </View>
        ) : categories.length === 0 ? (
          <View style={styles.noCategoriesContainer}>
            <Text style={styles.noCategoriesText}>{STRINGS.noCategories}</Text>
          </View>
        ) : (
          renderCategories
        )}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: COLORS.backgroundColor,
  },
  scrollContainer: {
    flexGrow: 1,
    paddingHorizontal: 16,
    paddingBottom: 150,
    alignItems: "stretch",
  },
  datePickerContainer: {
    alignSelf: "flex-start",
    marginTop: 24,
    marginBottom: 12,
  },
  datePickerLabel: {
    fontSize: 16,
    fontWeight: "600",
    color: COLORS.textDarker,
    marginBottom: 16,
    textAlign: "left",
  },
  container: {
    width: "100%",
    marginTop: 16,
  },
  heading: {
    fontSize: 16,
    marginBottom: 16,
    fontWeight: "600",
    color: COLORS.textDarker,
    textAlign: "left",
  },
  checkboxContainer: {
    marginBottom: 12,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: COLORS.backgroundColor,
    paddingTop: 16,
    paddingBottom: 32,
    gap: 16,
  },
  loadingContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    marginBottom: 16,
  },
  loadingText: {
    fontSize: 14,
    color: COLORS.textDark,
  },
  noCategoriesContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 16,
  },
  noCategoriesText: {
    fontSize: 14,
    color: COLORS.textDark,
  },
});

export default FiltersView;
